/** Server-enforced permission keys. Roles are data; this list is the vocabulary (step 1 §87 plus S1 keys). */
export const permissionKeys = [
  'dashboard.read',
  'users.read',
  'users.invite',
  'users.manage',
  'roles.read',
  'roles.manage',
  'landings.read',
  'landings.create',
  'landings.edit',
  'landings.publish',
  'landings.delete',
  'content.read',
  'content.edit',
  'content.publish',
  'seo.read',
  'seo.manage',
  'forms.read',
  'forms.edit',
  'leads.read',
  'leads.read.all',
  'leads.create',
  'leads.edit',
  'leads.assign',
  'leads.qualify',
  'leads.delete',
  'leads.export',
  'leads.pii.read',
  'deals.read',
  'deals.edit',
  'deals.manage',
  'activities.read',
  'activities.write',
  'tasks.read',
  'tasks.manage',
  'events.read',
  'ads.read',
  'ads.edit',
  'ads.approve',
  'ads.publish',
  'ads.budget.manage',
  'ads.connections.manage',
  'analytics.read',
  'analytics.export',
  'prospects.read',
  'prospects.manage',
  'outreach.manage',
  'integrations.read',
  'integrations.manage',
  'audit.read',
  'settings.read',
  'settings.manage',
] as const;
export type PermissionKey = (typeof permissionKeys)[number];

/** Grouping for the role matrix UI. Every key must appear exactly once (tested). */
export const permissionGroups: { group: string; keys: PermissionKey[] }[] = [
  { group: 'Workspace', keys: ['dashboard.read', 'events.read', 'settings.read', 'settings.manage', 'audit.read'] },
  { group: 'Team and access', keys: ['users.read', 'users.invite', 'users.manage', 'roles.read', 'roles.manage'] },
  { group: 'Website', keys: ['landings.read', 'landings.create', 'landings.edit', 'landings.publish', 'landings.delete', 'content.read', 'content.edit', 'content.publish', 'seo.read', 'seo.manage', 'forms.read', 'forms.edit'] },
  { group: 'Leads and sales', keys: ['leads.read', 'leads.read.all', 'leads.create', 'leads.edit', 'leads.assign', 'leads.qualify', 'leads.delete', 'leads.export', 'leads.pii.read', 'deals.read', 'deals.edit', 'deals.manage', 'activities.read', 'activities.write', 'tasks.read', 'tasks.manage'] },
  { group: 'Advertising', keys: ['ads.read', 'ads.edit', 'ads.approve', 'ads.publish', 'ads.budget.manage', 'ads.connections.manage'] },
  { group: 'Analytics and outreach', keys: ['analytics.read', 'analytics.export', 'prospects.read', 'prospects.manage', 'outreach.manage'] },
  { group: 'Integrations', keys: ['integrations.read', 'integrations.manage'] },
];

type RoleDef = { name: string; description: string; permissions: PermissionKey[] };
const all = [...permissionKeys];
const without = (...drop: PermissionKey[]) => all.filter((p) => !drop.includes(p));

/**
 * The ten default roles from step 1 §86. Notes on deliberate choices:
 * - spending money (`ads.publish`, `ads.budget.manage`) and approving it stay with the Owner (§63, §165);
 * - publishing a landing is outward-facing, so Marketing Manager and Content Editor can prepare but not publish
 *   (S1 verified this; step 1 §165's `landings.*` is read as "everything except the outward-facing publish step").
 */
export const defaultRoles: Record<string, RoleDef> = {
  owner: { name: 'Owner', description: 'Everything, including spending and access control.', permissions: all },
  administrator: {
    name: 'Administrator',
    description: 'Runs the workspace day to day; cannot publish ads or change budgets.',
    permissions: without('ads.publish', 'ads.budget.manage'),
  },
  'marketing-manager': {
    name: 'Marketing Manager',
    description: 'Landings, content, SEO, forms, ad drafts and analytics. No publishing, spend, team management or lead contact details.',
    permissions: ['dashboard.read', 'events.read', 'landings.read', 'landings.create', 'landings.edit', 'content.read', 'content.edit', 'seo.read', 'seo.manage', 'forms.read', 'forms.edit', 'leads.read', 'leads.read.all', 'ads.read', 'ads.edit', 'analytics.read', 'integrations.read'],
  },
  'ads-manager': {
    name: 'Ads Manager',
    description: 'Prepares and reviews campaigns and creatives; cannot publish or change budgets.',
    permissions: ['dashboard.read', 'events.read', 'landings.read', 'leads.read', 'leads.read.all', 'ads.read', 'ads.edit', 'analytics.read', 'integrations.read'],
  },
  'content-editor': {
    name: 'Content Editor',
    description: 'Writes and edits landing and page content; cannot publish.',
    permissions: ['dashboard.read', 'landings.read', 'landings.create', 'landings.edit', 'content.read', 'content.edit', 'seo.read', 'forms.read'],
  },
  'sales-manager': {
    name: 'Sales Manager',
    description: 'All leads and deals, assignment and qualification, with contact details.',
    permissions: ['dashboard.read', 'events.read', 'users.read', 'leads.read', 'leads.read.all', 'leads.create', 'leads.edit', 'leads.assign', 'leads.qualify', 'leads.export', 'leads.pii.read', 'deals.read', 'deals.edit', 'deals.manage', 'activities.read', 'activities.write', 'tasks.read', 'tasks.manage', 'analytics.read', 'prospects.read'],
  },
  salesperson: {
    name: 'Sales Representative',
    description: 'Works the leads assigned to them, with contact details and deals.',
    permissions: ['dashboard.read', 'leads.read', 'leads.create', 'leads.edit', 'leads.qualify', 'leads.pii.read', 'deals.read', 'deals.edit', 'activities.read', 'activities.write', 'tasks.read', 'tasks.manage', 'prospects.read'],
  },
  analyst: {
    name: 'Analyst',
    description: 'Read-only analytics and advertising reporting; no mutations.',
    permissions: ['dashboard.read', 'events.read', 'landings.read', 'leads.read', 'leads.read.all', 'ads.read', 'analytics.read'],
  },
  developer: {
    name: 'Developer',
    description: 'Read access to configuration, integrations and the audit log for technical support.',
    permissions: ['dashboard.read', 'events.read', 'landings.read', 'forms.read', 'roles.read', 'integrations.read', 'settings.read', 'audit.read'],
  },
  viewer: {
    name: 'Viewer',
    description: 'Read-only overview of landings, content and reports.',
    permissions: ['dashboard.read', 'landings.read', 'content.read', 'seo.read', 'analytics.read'],
  },
};

/** Roles that grant control over access or money; a non-owner can never grant what they do not hold. */
export const privilegedPermissions: ReadonlySet<PermissionKey> = new Set(['users.manage', 'roles.manage', 'ads.publish', 'ads.budget.manage', 'ads.connections.manage', 'integrations.manage', 'leads.export', 'leads.delete', 'settings.manage']);
